/**
 * @fileoverview Onboarding state management hook with localStorage persistence.
 * @module features/onboarding/hooks/useOnboardingState
 */

import { useState, useCallback, useEffect } from 'react'

/** All onboarding step identifiers */
export type OnboardingStep =
  | 'welcome'
  | 'create-org'
  | 'currency'
  | 'sales-channel'
  | 'google-ads'
  | 'facebook'
  | 'bank'
  | 'completion'

/** Ordered list of all steps */
export const ONBOARDING_STEPS: OnboardingStep[] = [
  'create-org',
  'welcome',
  'currency',
  'sales-channel',
  'google-ads',
  'facebook',
  'bank',
  'completion',
]

/** Steps visible in the stepper (excludes create-org) */
export const VISIBLE_STEPS: OnboardingStep[] = ONBOARDING_STEPS.filter(s => s !== 'create-org')

/** localStorage key for persisting onboarding state */
const STORAGE_KEY = 'abyssblock_onboarding_state'

/** Persisted state shape */
interface PersistedState {
  currentStep: OnboardingStep
  completedSteps: OnboardingStep[]
  orgId: string | null
  orgName: string
  currency: string | null
}

/** Full state including derived values */
export interface OnboardingState extends PersistedState {
  /** Current step index (0-based) */
  currentStepIndex: number
  /** Total number of steps */
  totalSteps: number
  /** Completed step indices for StepIndicator */
  completedStepIndices: number[]
  /** Whether org has been created (enables persistence) */
  hasOrg: boolean
  /** Whether to show the stepper (hidden during create-org) */
  showStepper: boolean
  /** Current step index in visible steps (for stepper) */
  visibleStepIndex: number
  /** Total visible steps (for stepper) */
  visibleTotalSteps: number
  /** Completed visible step indices (for stepper) */
  visibleCompletedIndices: number[]
}

/** Hook return type */
export interface UseOnboardingStateReturn {
  state: OnboardingState
  /** Advance to next step */
  nextStep: () => void
  /** Go to previous step */
  prevStep: () => void
  /** Jump to specific step */
  goToStep: (step: OnboardingStep) => void
  /** Mark current step as completed and advance */
  completeStep: () => void
  /** Skip current step (mark as completed without data) */
  skipStep: () => void
  /** Set organization data after creation */
  setOrgData: (orgId: string, orgName: string) => void
  /** Set currency after selection */
  setCurrency: (currency: string) => void
  /** Reset onboarding state */
  reset: () => void
}

const defaultState: PersistedState = {
  currentStep: 'create-org',
  completedSteps: [],
  orgId: null,
  orgName: '',
  currency: null,
}

/**
 * Load persisted state from localStorage.
 * Only loads if org was created (persistence enabled).
 * Includes migration for renamed steps.
 */
function loadPersistedState(): PersistedState | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null

    const parsed = JSON.parse(stored) as PersistedState
    // Only restore if org was created
    if (!parsed.orgId) return null

    // Migration: 'baselinker' → 'sales-channel' (renamed in onboarding v2)
    if ((parsed.currentStep as string) === 'baselinker') {
      parsed.currentStep = 'sales-channel'
    }
    parsed.completedSteps = parsed.completedSteps.map(step =>
      (step as string) === 'baselinker' ? 'sales-channel' : step
    ) as OnboardingStep[]

    return parsed
  } catch {
    return null
  }
}

/**
 * Save state to localStorage.
 * Only persists if org was created.
 */
function persistState(state: PersistedState): void {
  // Only persist after org is created
  if (!state.orgId) return

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clear persisted onboarding state.
 * Exported for use in logout flow.
 */
export function clearOnboardingState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore storage errors
  }
}

/**
 * Hook for managing onboarding wizard state.
 * Persists progress to localStorage after organization is created.
 *
 * @returns Onboarding state and actions
 *
 * @example
 * ```tsx
 * function OnboardingPage() {
 *   const { state, nextStep, completeStep } = useOnboardingState()
 *
 *   return (
 *     <OnboardingLayout currentStep={state.currentStepIndex} ...>
 *       {state.currentStep === 'welcome' && (
 *         <WelcomeStep onContinue={completeStep} />
 *       )}
 *     </OnboardingLayout>
 *   )
 * }
 * ```
 */
export function useOnboardingState(): UseOnboardingStateReturn {
  const [persistedState, setPersistedState] = useState<PersistedState>(() => {
    const loaded = loadPersistedState()
    return loaded ?? defaultState
  })

  // Persist state changes to localStorage
  useEffect(() => {
    persistState(persistedState)
  }, [persistedState])

  // Compute derived state
  const currentStepIndex = ONBOARDING_STEPS.indexOf(persistedState.currentStep)
  const completedStepIndices = persistedState.completedSteps.map(step =>
    ONBOARDING_STEPS.indexOf(step)
  )

  // Compute visible step indices (for stepper - excludes create-org)
  const showStepper = persistedState.currentStep !== 'create-org'
  const visibleStepIndex = VISIBLE_STEPS.indexOf(persistedState.currentStep)
  const visibleCompletedIndices = persistedState.completedSteps
    .filter(step => step !== 'create-org')
    .map(step => VISIBLE_STEPS.indexOf(step))

  const state: OnboardingState = {
    ...persistedState,
    currentStepIndex,
    totalSteps: ONBOARDING_STEPS.length,
    completedStepIndices,
    hasOrg: persistedState.orgId !== null,
    showStepper,
    visibleStepIndex,
    visibleTotalSteps: VISIBLE_STEPS.length,
    visibleCompletedIndices,
  }

  const nextStep = useCallback(() => {
    setPersistedState(prev => {
      const currentIndex = ONBOARDING_STEPS.indexOf(prev.currentStep)
      const nextIndex = Math.min(currentIndex + 1, ONBOARDING_STEPS.length - 1)
      const nextStepValue = ONBOARDING_STEPS[nextIndex]
      return {
        ...prev,
        currentStep: nextStepValue ?? prev.currentStep,
      }
    })
  }, [])

  const prevStep = useCallback(() => {
    setPersistedState(prev => {
      const currentIndex = ONBOARDING_STEPS.indexOf(prev.currentStep)
      const prevIndex = Math.max(currentIndex - 1, 0)
      const prevStepValue = ONBOARDING_STEPS[prevIndex]
      return {
        ...prev,
        currentStep: prevStepValue ?? prev.currentStep,
      }
    })
  }, [])

  const goToStep = useCallback((step: OnboardingStep) => {
    setPersistedState(prev => ({
      ...prev,
      currentStep: step,
    }))
  }, [])

  const completeStep = useCallback(() => {
    setPersistedState(prev => {
      const completedSteps = prev.completedSteps.includes(prev.currentStep)
        ? prev.completedSteps
        : [...prev.completedSteps, prev.currentStep]

      const currentIndex = ONBOARDING_STEPS.indexOf(prev.currentStep)
      const nextIndex = Math.min(currentIndex + 1, ONBOARDING_STEPS.length - 1)
      const nextStepValue = ONBOARDING_STEPS[nextIndex]

      return {
        ...prev,
        completedSteps,
        currentStep: nextStepValue ?? prev.currentStep,
      }
    })
  }, [])

  const skipStep = useCallback(() => {
    // Skip behaves same as complete for optional steps
    completeStep()
  }, [completeStep])

  const setOrgData = useCallback((orgId: string, orgName: string) => {
    setPersistedState(prev => ({
      ...prev,
      orgId,
      orgName,
    }))
  }, [])

  const setCurrency = useCallback((currency: string) => {
    setPersistedState(prev => ({
      ...prev,
      currency,
    }))
  }, [])

  const reset = useCallback(() => {
    clearOnboardingState()
    setPersistedState(defaultState)
  }, [])

  return {
    state,
    nextStep,
    prevStep,
    goToStep,
    completeStep,
    skipStep,
    setOrgData,
    setCurrency,
    reset,
  }
}
